import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Package, Ship } from 'lucide-react';
import { CashflowChart } from '../../components/charts/CashflowChart';
import { GradeBadge } from '../../components/GradeBadge';
import { Notice, Panel, Tabs } from '../../components/ui';
import { humanPath, num, pct, usd, usdT } from '../../lib/format';
import type { OptionResult } from '../../types/api';
import { Levers } from './Levers';

type Tab = 'levers' | 'trade' | 'costs' | 'capital' | 'cashflow' | 'carbon' | 'confidence';

export function OptionDetail({ option }: { option: OptionResult }) {
  const [tab, setTab] = useState<Tab>('levers');
  const o = option;
  return (
    <Panel
      title={`${o.location_name} · ${o.route_name}`}
      description={`Rank ${o.rank} · score ${o.score.toFixed(1)} · IRR ${pct(o.irr)} · payback ${o.payback_years != null ? `${o.payback_years} yrs` : 'not reached'}`}
      action={<GradeBadge grade={o.confidence.worst} withLabel />}
      flush
    >
      <Tabs<Tab>
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'levers', label: 'Negotiating levers' },
          { value: 'trade', label: 'Trade & Counterparties' },
          { value: 'costs', label: 'Cost per tonne' },
          { value: 'capital', label: 'Capital & incentives' },
          { value: 'cashflow', label: 'Cash flow' },
          { value: 'carbon', label: 'Carbon & risk' },
          { value: 'confidence', label: 'Data confidence' },
        ]}
      />
      <div className="panel-body">
        {o.notes.map((n) => <Notice key={n} tone="warn">{n}</Notice>)}
        {tab === 'levers' && <Levers option={o} />}
        {tab === 'trade' && <TradeTab o={o} />}
        {tab === 'costs' && <CostTab o={o} />}
        {tab === 'capital' && <CapitalTab o={o} />}
        {tab === 'cashflow' && (o.cashflow ? <CashflowTab o={o} /> : <p className="muted small">Cash-flow table not included in this result.</p>)}
        {tab === 'carbon' && <CarbonTab o={o} />}
        {tab === 'confidence' && <ConfidenceTab o={o} />}
      </div>
    </Panel>
  );
}

function CostTab({ o }: { o: OptionResult }) {
  return (
    <div className="two-col">
      <dl className="kv">
        {Object.entries(o.cost_breakdown_usd_t).map(([k, v]) => (
          <div key={k} style={{ display: 'contents' }}><dt>{k}</dt><dd>{usdT(v)} <span className="tiny muted">{pct(v / o.cash_cost_usd_t, 0)}</span></dd></div>
        ))}
        <dt className="total">Cash cost</dt><dd className="total">{usdT(o.cash_cost_usd_t)}</dd>
        <dt>Capital charge</dt><dd>{usdT(o.capital_charge_usd_t)}</dd>
        <dt className="total">All-in cost</dt><dd className="total">{usdT(o.all_in_cost_usd_t)}</dd>
      </dl>
      <dl className="kv">
        <dt>LiOH benchmark</dt><dd>{usdT(o.prices.lioh_benchmark_usd_t)}</dd>
        {o.prices.import_parity_uplift_usd_t > 0 && <><dt>Import-parity uplift</dt><dd>+{usdT(o.prices.import_parity_uplift_usd_t)}</dd></>}
        <dt>By-product credit (Na₂SO₄)</dt><dd>{usdT(o.byproduct_credit_usd_t)}</dd>
        <dt className="total">Revenue per tonne</dt><dd className="total">{usdT(o.revenue_usd_t)}</dd>
        <dt>Cash margin</dt><dd style={{ color: o.margin_usd_t >= 0 ? 'var(--good)' : 'var(--bad)' }}>{usdT(o.margin_usd_t)}</dd>
        <dt>SC6 price ({o.prices.feed_pricing})</dt><dd>{usdT(o.prices.sc6_usd_t)}</dd>
        <dt>SC6 per t LiOH</dt><dd>{num(o.sc6_t_per_t, 2)} t</dd>
        <dt>Conversion spread</dt><dd>{usdT(o.conversion_spread_usd_t)}</dd>
        <dt>Workforce</dt><dd>{num(o.workforce_fte)} FTE</dd>
      </dl>
    </div>
  );
}

function CapitalTab({ o }: { o: OptionResult }) {
  const c = o.capex;
  const inc = o.incentives;
  return (
    <div className="two-col">
      <dl className="kv">
        <dt>Direct</dt><dd>{usd(c.direct)}</dd>
        <dt>Land</dt><dd>{usd(c.land)}</dd>
        <dt>Owner's cost</dt><dd>{usd(c.owners)}</dd>
        <dt>Contingency</dt><dd>{usd(c.contingency)}</dd>
        <dt className="total">Gross capex</dt><dd className="total">{usd(c.gross)}</dd>
        <dt>State capex subsidy</dt><dd>−{usd(c.subsidy)}</dd>
        <dt className="total">Net capex</dt><dd className="total">{usd(c.net)}</dd>
        <dt>Net per t/yr capacity</dt><dd>{usd(c.usd_per_tpa_net)}</dd>
        <dt>Construction phasing</dt><dd>{c.construction_phasing.map((w) => pct(w, 0)).join(' / ')}</dd>
      </dl>
      <div className="stack" style={{ gap: 10 }}>
        <dl className="kv">
          <dt>Subsidy disbursement</dt><dd>{inc.capex_subsidy_disbursement_years ? `${inc.capex_subsidy_disbursement_years} yrs after COD` : 'upfront'}</dd>
          <dt>Power rebate</dt><dd>{usdT(inc.power_rebate_usd_t)} × {inc.power_rebate_years} yrs</dd>
          <dt>SGST reimbursement</dt><dd>{usdT(inc.sgst_reimbursement_usd_t)} × {inc.sgst_reimbursement_years} yrs</dd>
          {inc.production_tax_incentive_usd_t > 0 && <><dt>AU production tax incentive</dt><dd>{usdT(inc.production_tax_incentive_usd_t)} × {inc.production_tax_incentive_years} yrs</dd></>}
        </dl>
        {o.equity && (
          <dl className="kv">
            <dt className="total">Debt at COD</dt><dd className="total">{usd(o.equity.debt_at_cod)}</dd>
            <dt>Annual debt service</dt><dd>{usd(o.equity.annual_debt_service)}</dd>
            <dt>Equity IRR</dt><dd>{pct(o.equity.equity_irr)}</dd>
            <dt>Minimum DSCR</dt><dd style={{ color: (o.equity.min_dscr ?? 0) < 1.2 ? 'var(--bad)' : undefined }}>{o.equity.min_dscr != null ? `${o.equity.min_dscr.toFixed(2)}×` : 'n/a'}</dd>
            <dt>Average DSCR</dt><dd>{o.equity.avg_dscr != null ? `${o.equity.avg_dscr.toFixed(2)}×` : 'n/a'}</dd>
          </dl>
        )}
        <p className="tiny muted">State incentive packages are grade C until a term sheet is negotiated. Subsidies are not taxed as income; they reduce the depreciable base.</p>
      </div>
    </div>
  );
}

function CashflowTab({ o }: { o: OptionResult }) {
  const rows = o.cashflow ?? [];
  return (
    <div className="stack">
      <CashflowChart rows={rows} />
      <div className="legend">
        <span><i style={{ background: 'var(--s-capital)' }} />Construction</span>
        <span><i style={{ background: 'var(--s-pos)' }} />Positive FCF</span>
        <span><i style={{ background: 'var(--s-neg)' }} />Negative FCF</span>
        <span><i style={{ background: 'var(--navy)', height: 2 }} />Cumulative</span>
      </div>
      <details className="disclosure" style={{ borderTop: 0 }}>
        <summary style={{ paddingLeft: 0 }}>Show annual table</summary>
        <div className="table-wrap" style={{ padding: 0 }}>
          <table>
            <thead><tr><th>Year</th><th className="num">Production t</th><th className="num">Revenue</th><th className="num">EBITDA</th><th className="num">Tax</th><th className="num">Incentives</th><th className="num">FCF</th><th className="num">Cumulative</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.year}>
                  <td>{r.year}{r.phase === 'construction' ? ' (build)' : ''}</td>
                  <td className="num">{num(r.production)}</td><td className="num">{usd(r.revenue)}</td><td className="num">{usd(r.ebitda)}</td>
                  <td className="num">{usd(r.tax)}</td><td className="num">{usd(r.incentives_taxable + r.incentives_non_taxable)}</td>
                  <td className="num">{usd(r.fcf)}</td><td className="num">{usd(r.cumulative_fcf)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

function CarbonTab({ o }: { o: OptionResult }) {
  const c = o.carbon_t_co2_per_t;
  const parts = [
    { k: 'Grid power', v: c.grid, color: 'var(--s-feed)' },
    { k: 'Thermal (gas)', v: c.thermal, color: 'var(--s-logistics)' },
    { k: 'Reagents (embodied)', v: c.reagents, color: 'var(--s-conversion)' },
    { k: 'Transport', v: c.logistics, color: 'var(--s-capital)' },
  ];
  return (
    <div className="two-col">
      <div className="stack" style={{ gap: 10 }}>
        <div style={{ display: 'flex', height: 18, borderRadius: 4, overflow: 'hidden' }} aria-label={`Carbon ${c.total.toFixed(2)} tCO2 per tonne`}>
          {parts.map((p) => <div key={p.k} title={`${p.k}: ${p.v.toFixed(2)}`} style={{ width: `${(p.v / c.total) * 100}%`, background: p.color }} />)}
        </div>
        <dl className="kv">
          {parts.map((p) => <div key={p.k} style={{ display: 'contents' }}><dt><span className="legend"><span><i style={{ background: p.color }} />{p.k}</span></span></dt><dd>{p.v.toFixed(2)} t</dd></div>)}
          <dt className="total">Total tCO₂ per t LiOH</dt><dd className="total">{c.total.toFixed(2)}</dd>
        </dl>
      </div>
      <dl className="kv">
        <dt>Technology risk (1–5)</dt><dd>{o.risk.technology}</dd>
        <dt>Water stress (1–5)</dt><dd>{o.risk.water_stress}</dd>
        <dt>Established chemical cluster</dt><dd>{o.risk.chemical_cluster ? 'Yes' : 'No'}</dd>
        <dt className="total">Score components</dt><dd className="total">{o.score.toFixed(1)}</dd>
        {Object.entries(o.score_components).map(([k, v]) => <div key={k} style={{ display: 'contents' }}><dt>{k}</dt><dd>{v.toFixed(1)}</dd></div>)}
      </dl>
    </div>
  );
}

function ConfidenceTab({ o }: { o: OptionResult }) {
  const c = o.confidence;
  return (
    <div className="stack">
      <div className="row">
        <GradeBadge grade="A" /> <span className="small">{c.counts.A} verified</span>
        <GradeBadge grade="B" /> <span className="small">{c.counts.B} indicative</span>
        <GradeBadge grade="C" /> <span className="small">{c.counts.C} placeholder</span>
        {c.overridden_inputs.length > 0 && <><GradeBadge grade="B*" /> <span className="small">{c.overridden_inputs.length} overridden by you</span></>}
      </div>
      {c.c_grade_inputs.length > 0 && (
        <Notice tone="warn">
          <p><strong>Grade-C inputs behind this result.</strong> Replace them with verified data before using the numbers externally.</p>
          <div className="row" style={{ marginTop: 8 }}>{c.c_grade_inputs.map((p) => <span key={p} className="chip path" title={p}>{humanPath(p)}</span>)}</div>
        </Notice>
      )}
      {c.overridden_inputs.length > 0 && (
        <div><p className="small muted" style={{ marginBottom: 6 }}>Your overrides (marked * in exports):</p>
          <div className="row">{c.overridden_inputs.map((p) => <span key={p} className="chip path">{humanPath(p)} *</span>)}</div></div>
      )}
      <p className="tiny muted">See the Uncertainty page for which of these inputs moves the breakeven most — that is the order in which to verify them.</p>
    </div>
  );
}

function TradeTab({ o }: { o: OptionResult }) {
  const isIndia = o.country === 'IN';
  return (
    <div className="stack" style={{ gap: 16 }}>
      <p className="small muted">
        Direct commercial execution routes for <strong>{o.location_name}</strong> under the Australia–India critical minerals corridor.
      </p>
      <div className="card-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
        {isIndia ? (
          <>
            <div className="panel" style={{ padding: 14 }}>
              <div className="row" style={{ gap: 8, marginBottom: 6 }}>
                <Ship size={16} />
                <strong>Upstream Feed Suppliers</strong>
              </div>
              <p className="tiny muted" style={{ marginBottom: 10 }}>
                Verified Australian miners supplying spodumene concentrate (SC6) shipping to {o.location_name.split('(')[0].trim()} under 0% ECTA tariff.
              </p>
              <Link className="button secondary sm" to="/trade/directory?kind=seller&product_id=spodumene_sc6&country=AU">
                View Australian Miners <ArrowRight size={13} style={{ marginLeft: 4 }} />
              </Link>
            </div>

            <div className="panel" style={{ padding: 14 }}>
              <div className="row" style={{ gap: 8, marginBottom: 6 }}>
                <Building2 size={16} />
                <strong>Domestic Battery Offtakers</strong>
              </div>
              <p className="tiny muted" style={{ marginBottom: 10 }}>
                Indian battery cell gigafactories and cathode manufacturers buying battery-grade LiOH and carbonate.
              </p>
              <Link className="button secondary sm" to="/trade/directory?kind=buyer&country=IN">
                View Indian Cell Makers <ArrowRight size={13} style={{ marginLeft: 4 }} />
              </Link>
            </div>

            <div className="panel" style={{ padding: 14 }}>
              <div className="row" style={{ gap: 8, marginBottom: 6 }}>
                <Package size={16} />
                <strong>Urban Mining & Recyclers</strong>
              </div>
              <p className="tiny muted" style={{ marginBottom: 10 }}>
                Authorized Indian recyclers trading black mass, e-waste PCB scrap, and end-of-life battery cells under CPCB EPR rules.
              </p>
              <Link className="button secondary sm" to="/trade/directory?kind=buyer&category=recycling&country=IN">
                View Authorized Recyclers <ArrowRight size={13} style={{ marginLeft: 4 }} />
              </Link>
            </div>
          </>
        ) : (
          <>
            <div className="panel" style={{ padding: 14 }}>
              <div className="row" style={{ gap: 8, marginBottom: 6 }}>
                <Building2 size={16} />
                <strong>Indian Lithium Offtakers</strong>
              </div>
              <p className="tiny muted" style={{ marginBottom: 10 }}>
                Indian battery manufacturers seeking direct export of battery-grade lithium hydroxide from Western Australia.
              </p>
              <Link className="button secondary sm" to="/trade/directory?kind=buyer&product_id=lioh_battery&country=IN">
                View Indian Offtakers <ArrowRight size={13} style={{ marginLeft: 4 }} />
              </Link>
            </div>

            <div className="panel" style={{ padding: 14 }}>
              <div className="row" style={{ gap: 8, marginBottom: 6 }}>
                <Ship size={16} />
                <strong>Corridor Logistics & Ports</strong>
              </div>
              <p className="tiny muted" style={{ marginBottom: 10 }}>
                Official port pairings, freight rates, and customs documentation from Fremantle to Mundra/JNPT.
              </p>
              <Link className="button secondary sm" to="/trade/corridor?exporter=AU&importer=IN">
                View AU → IN Corridor <ArrowRight size={13} style={{ marginLeft: 4 }} />
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
